import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { paymentService } from '@/api/domains/payment';
import { useHybridExport } from '@/hooks/ui/useHybridExport';
import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';
import type { UIExportFormat } from '@/types/fileExport.types';


function joinOrUndefined(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

export interface UsePaymentConceptsExportParams {
  monthlyCutId: number;
  employeeIds: number[];
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: EmployeeFilteredStatus;
  searchText: string | undefined;
}

export interface UsePaymentConceptsExportResult {
  isExporting: boolean;
  onExport: (format: UIExportFormat) => Promise<void>;
}

/**
 * Descarga a Excel del reporte de conceptos de pago (ASIS-5320). Estrategia híbrida sync/async:
 * el backend responde 200 con el archivo cuando el volumen está bajo el umbral síncrono, o 202
 * (job en background) cuando lo supera. Envía los mismos filtros que el listado — incluido el
 * texto de búsqueda activo — sin paginación: exporta todo el universo que cumple los filtros.
 *
 * Delega el branching sync/202, el estado `isExporting` y las notificaciones en `useHybridExport`
 * (análogo a `useExport` pero con soporte para la rama asíncrona 202); aquí solo se arma el payload
 * y se traducen los textos.
 */
export function usePaymentConceptsExport(
  params: UsePaymentConceptsExportParams,
): UsePaymentConceptsExportResult {
  const { t: translate } = useTranslation('payment');

  const requestExport = useCallback(
    () => paymentService.exportPaymentConcepts({
      monthlyCutId: params.monthlyCutId,
      employeeIds: joinOrUndefined(params.employeeIds.map(String)),
      companyIds: joinOrUndefined(params.companyIds),
      areaIds: joinOrUndefined(params.areaIds),
      positionIds: joinOrUndefined(params.positionIds),
      costCenterIds: joinOrUndefined(params.costCenterIds),
      contractModalityIds: joinOrUndefined(params.contractModalityIds),
      groupIds: joinOrUndefined(params.groupIds),
      employeeStatus: params.employeeStatus,
      searchText: params.searchText || undefined,
    }),
    [params],
  );

  const { isExporting, runExport } = useHybridExport({
    requestExport,
    messages: {
      error: translate('list.messages.exportError'),
      success: translate('list.messages.exportSuccess'),
      queued: translate('list.messages.exportQueued'),
    },
  });

  return { isExporting, onExport: runExport };
}

export default usePaymentConceptsExport;
